import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrandingService } from "@root/services/admin/branding";
import {
  DEFAULT_BRANDING_CONFIG,
  DEFAULT_BRANDING_COLORS,
  DEFAULT_BRANDING_TYPOGRAPHY,
  DEFAULT_BRANDING_METADATA,
  DEFAULT_BRANDING_EMAIL,
  DEFAULT_BRANDING_LOGIN_PAGE,
  DEFAULT_BRANDING_LOGO,
  BRANDING_CSS_VAR_PREFIX,
  DEFAULT_APP_TITLE,
  DEFAULT_FAVICON_PATH,
} from "@root/constants/branding";
import {
  generateFaviconFromDomain,
  getOrganizationDisplayName,
} from "@root/utils/branding";

const camelToKebab = (str: string): string =>
  str.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

const injectCssVariables = (colors: IBrandingColors): void => {
  const root = document.documentElement;
  Object.entries(colors).forEach(([key, value]) => {
    const cssVarName = `${BRANDING_CSS_VAR_PREFIX}-${camelToKebab(key)}`;
    root.style.setProperty(cssVarName, value as string);
  });
};

const updateFavicon = (url: string): void => {
  // Remove existing favicons
  document
    .querySelectorAll("link[rel*='icon']")
    .forEach((link) => link.remove());

  // Add new favicon
  const link = document.createElement("link");
  link.rel = "icon";
  if (url.startsWith("data:")) {
    // Handle data URLs (SVG data URLs)
    link.type = "image/svg+xml";
    link.href = url;
  } else if (url.endsWith(".svg")) {
    link.type = "image/svg+xml";
    link.href = url;
  } else if (url.endsWith(".png")) {
    link.type = "image/png";
    link.href = url;
  } else if (url.endsWith(".ico")) {
    link.type = "image/x-icon";
    link.href = url;
  } else {
    // Default to the URL as-is
    link.href = url;
  }
  document.head.appendChild(link);

  // iOS support
  const appleLink = document.createElement("link");
  appleLink.rel = "apple-touch-icon";
  appleLink.href = url;
  document.head.appendChild(appleLink);
};

const mergeBrandingWithDefaults = (
  apiBranding: Partial<IBrandingConfig>
): IBrandingConfig => {
  return {
    ...DEFAULT_BRANDING_CONFIG,
    ...apiBranding,
    logo: { ...DEFAULT_BRANDING_LOGO, ...apiBranding.logo },
    colors: { ...DEFAULT_BRANDING_COLORS, ...apiBranding.colors },
    typography: { ...DEFAULT_BRANDING_TYPOGRAPHY, ...apiBranding.typography },
    metadata: { ...DEFAULT_BRANDING_METADATA, ...apiBranding.metadata },
    emailBranding: { ...DEFAULT_BRANDING_EMAIL, ...apiBranding.emailBranding },
    loginPage: { ...DEFAULT_BRANDING_LOGIN_PAGE, ...apiBranding.loginPage },
  };
};

export const useBranding = (): IBrandingContextValue => {
  const [branding, setBranding] = useState<IBrandingConfig | null>(null);
  const [organizationBranding, setOrganizationBranding] =
    useState<IOrganizationBranding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const initializedRef = useRef(false);

  const brandingService = useMemo(() => new BrandingService(), []);
  const baseUrl = process.env.REACT_APP_BASE_URL || "";

  // Compute derived values
  const logoUrl = useMemo((): string | null => {
    if (organizationBranding?.logoSignedUrl) return organizationBranding.logoSignedUrl;
    if (organizationBranding?.logo) return organizationBranding.logo;
    if (branding?.logo?.light) return branding.logo.light;
    return null;
  }, [organizationBranding, branding]);

  const faviconUrl = useMemo((): string | null => {
    if (organizationBranding?.faviconSignedUrl) return organizationBranding.faviconSignedUrl;
    if (organizationBranding?.favicon) return organizationBranding.favicon;
    if (branding?.logo?.favicon) return branding.logo.favicon;
    return null;
  }, [organizationBranding, branding]);

  const organizationName = useMemo((): string => {
    if (organizationBranding?.name) return organizationBranding.name;
    if (branding?.metadata?.companyName) return branding.metadata.companyName;
    return DEFAULT_APP_TITLE;
  }, [organizationBranding, branding]);

  const applyDefaultBranding = useCallback(() => {
    setBranding(DEFAULT_BRANDING_CONFIG);
    injectCssVariables(DEFAULT_BRANDING_COLORS);
    document.title = DEFAULT_APP_TITLE;
    
    // Generate default favicon from current domain
    const currentDomain = window.location.hostname;
    const defaultFavicon = generateFaviconFromDomain(currentDomain);
    updateFavicon(defaultFavicon);
  }, []);

  const detectOrganization = useCallback(async () => {
    const currentDomain = window.location.hostname;

    try {
      setIsLoading(true);
      setError(null);

      const response = await brandingService.fetchBrandingByDomain(
        baseUrl,
        currentDomain
      );

      if (response?.error) {
        // API returned error - use defaults
        applyDefaultBranding();
        return;
      }

      // Step 1: Resolve organization by domain
      // API: GET /api/org/public?domain={domain}
      // After prepareResponseObject unwraps, response.data =
      //   {
      //     id,
      //     name,
      //     email,
      //     domain,
      //     logo,
      //     favicon,
      //     createdAt,
      //     updatedAt
      //   }
      const orgData = response?.data;

      if (orgData && orgData.id) {
        // Map API response to IOrganizationBranding
        const orgBranding: IOrganizationBranding = {
          id: orgData.id,
          name: orgData.name || "",
          logo: orgData.logo || undefined,
          logoSignedUrl: orgData.logoSignedUrl || undefined,
          favicon: orgData.favicon || undefined,
          faviconSignedUrl: orgData.faviconSignedUrl || undefined,
          domain: orgData.domain || currentDomain,
        };
        setOrganizationBranding(orgBranding);
        setOrganizationId(orgData.id);

        // Step 2: Fetch full branding by organization ID
        // API: GET /api/org/public/{id}
        let resolvedConfig: IBrandingConfig | null = null;
        try {
          const brandingResponse = await brandingService.fetchBrandingById(
            baseUrl,
            orgData.id
          );

          if (!brandingResponse?.error && brandingResponse?.data) {
            // Backend may either return the branding config directly in data
            // or nested under data.branding
            const apiBranding =
              (brandingResponse.data as any)?.branding || brandingResponse.data;

            resolvedConfig = mergeBrandingWithDefaults({
              organizationId: String(orgData.id),
              ...(apiBranding as Partial<IBrandingConfig>),
              metadata: {
                companyName:
                  (apiBranding as any)?.metadata?.companyName ||
                  orgData.name ||
                  DEFAULT_APP_TITLE,
              },
            });
          }
        } catch (e) {
          // Ignore and fall back to defaults + org name below
        }

        // Fallback: if branding API failed or returned empty, use defaults + org name
        if (!resolvedConfig) {
          resolvedConfig = mergeBrandingWithDefaults({
            organizationId: String(orgData.id),
            metadata: {
              companyName: orgData.name || DEFAULT_APP_TITLE,
            },
          });
        }

        setBranding(resolvedConfig);
        injectCssVariables(resolvedConfig.colors);

        // Update document title
        document.title =
          resolvedConfig.metadata.companyName ||
          orgData.name ||
          DEFAULT_APP_TITLE;

        // Update favicon from resolved branding first, then org data
        const favicon =
          (resolvedConfig.logo?.favicon as string) ||
          orgData.faviconSignedUrl ||
          orgData.favicon;
        
        if (favicon) {
          updateFavicon(favicon);
        } else {
          // Generate default favicon from domain if no favicon available
          const defaultFavicon = generateFaviconFromDomain(
            orgData.domain || currentDomain
          );
          updateFavicon(defaultFavicon);
        }

        // Set subscription for feature gating (if backend returns it)
        if (orgData.subscription) {
          setSubscription(orgData.subscription);
        }
      } else {
        applyDefaultBranding();
      }
    } catch (err: any) {
      console.error("Branding fetch failed, using defaults:", err);
      setError(err?.statusText || "Failed to load branding");
      applyDefaultBranding();
    } finally {
      setIsLoading(false);
    }
  }, [brandingService, baseUrl, applyDefaultBranding]);

  const refreshBranding = useCallback(async () => {
    await detectOrganization();
  }, [detectOrganization]);

  const isFeatureEnabled = useCallback(
    (feature: string): boolean => {
      if (!subscription?.features) return true;
      return subscription.features.includes(feature);
    },
    [subscription]
  );

  // Fetch branding on mount (only once)
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      // Set initial default favicon based on current domain immediately
      const currentDomain = window.location.hostname;
      const initialFavicon = generateFaviconFromDomain(currentDomain);
      updateFavicon(initialFavicon);
      
      detectOrganization();
    }
  }, [detectOrganization]);

  // Update favicon when faviconUrl changes or when organization is detected
  useEffect(() => {
    if (faviconUrl) {
      // Use API-provided favicon
      updateFavicon(faviconUrl);
    } else if (!isLoading && organizationBranding) {
      // Generate default favicon from domain if no favicon URL is available
      const currentDomain = window.location.hostname;
      const domainToUse = organizationBranding.domain || currentDomain;
      const defaultFavicon = generateFaviconFromDomain(domainToUse);
      updateFavicon(defaultFavicon);
    }
  }, [faviconUrl, isLoading, organizationBranding]);

  // Update document title when organizationName changes
  useEffect(() => {
    if (organizationName && !isLoading) {
      document.title = organizationName;
    }
  }, [organizationName, isLoading]);

  return {
    branding,
    organizationBranding,
    isLoading,
    error,
    organizationId,
    logoUrl,
    faviconUrl,
    organizationName,
    refreshBranding,
    isFeatureEnabled,
  };
};
